export enum SpeechRecognitionStatus {
  IDLE = "idle",
  LISTENING = "listening",
  PROCESSING = "processing",
  ERROR = "error",
}

export enum SpeechRecognitionError {
  NOT_SUPPORTED = "not_supported",
  PERMISSION_DENIED = "permission_denied",
  NO_SPEECH = "no_speech",
  NETWORK = "network",
  UNKNOWN = "unknown",
}

export interface SpeechRecognitionSettings {
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
}

export interface SpeechRecognitionResult {
  transcript: string;
  confidence: number;
}

export type OnResultCallback = (result: SpeechRecognitionResult) => void;
export type OnStatusChangeCallback = (status: SpeechRecognitionStatus) => void;
export type OnErrorCallback = (error: SpeechRecognitionError) => void;

interface ISpeechRecognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  [index: number]: SpeechRecognitionResultItem;
  length: number;
}

interface SpeechRecognitionResultItem {
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
}

type SpeechRecognitionConstructor = new () => ISpeechRecognition;

export class SpeechRecognitionService {
  private recognition: ISpeechRecognition | null = null;
  private status: SpeechRecognitionStatus = SpeechRecognitionStatus.IDLE;
  private settings: SpeechRecognitionSettings;
  private _isStopping = false;

  private onResultCallback: OnResultCallback | null = null;
  private onStatusChangeCallback: OnStatusChangeCallback | null = null;
  private onErrorCallback: OnErrorCallback | null = null;

  constructor(settings: SpeechRecognitionSettings = {}) {
    this.settings = {
      lang: "pt-BR",
      continuous: false,
      interimResults: false,
      ...settings,
    };
  }

  public isSupported(): boolean {
    return !!(
      (typeof window !== "undefined" && (window as any).SpeechRecognition) ||
      (window as any).webkitSpeechRecognition
    );
  }

  private init(): void {
    if (!this.isSupported()) {
      this.handleError(SpeechRecognitionError.NOT_SUPPORTED);
      return;
    }

    const SpeechRecognitionAPI: SpeechRecognitionConstructor =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    this.recognition = new SpeechRecognitionAPI();
    this.recognition.lang = this.settings.lang!;
    this.recognition.continuous = this.settings.continuous!;
    this.recognition.interimResults = this.settings.interimResults!;

    this.recognition.onstart = () => {
      this.setStatus(SpeechRecognitionStatus.LISTENING);
    };

    this.recognition.onresult = (event: SpeechRecognitionEvent) => {
      this.setStatus(SpeechRecognitionStatus.PROCESSING);

      const lastResult = event.results[event.results.length - 1];

      if (lastResult.isFinal) {
        const alternative = lastResult[0];
        this.onResultCallback?.({
          transcript: alternative.transcript.trim().toLowerCase(),
          confidence: alternative.confidence,
        });
      }
    };

    this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      const errorMap: Record<string, SpeechRecognitionError> = {
        "not-allowed": SpeechRecognitionError.PERMISSION_DENIED,
        "no-speech": SpeechRecognitionError.NO_SPEECH,
        network: SpeechRecognitionError.NETWORK,
      };

      this.handleError(errorMap[event.error] ?? SpeechRecognitionError.UNKNOWN);
    };

    this.recognition.onend = () => {
      this._isStopping = false;

      if (this.status !== SpeechRecognitionStatus.ERROR) {
        this.setStatus(SpeechRecognitionStatus.IDLE);
      }
    };
  }

  public start(): void {
    if (!this.recognition) {
      this.init();
    }

    if (this.status === SpeechRecognitionStatus.LISTENING) {
      return;
    }

    try {
      this.recognition?.start();
    } catch (error) {
      console.error("[SpeechRecognitionService] Failed to start:", error);
      this.handleError(SpeechRecognitionError.UNKNOWN);
    }
  }

  public stop(): void {
    this._isStopping = true;
    this.recognition?.stop();
  }

  public abort(): void {
    this.recognition?.abort();
    this.setStatus(SpeechRecognitionStatus.IDLE);
  }

  public reset(): void {
    this.abort();
    this.recognition = null;
  }

  public getStatus(): SpeechRecognitionStatus {
    return this.status;
  }

  public onResult(callback: OnResultCallback): void {
    this.onResultCallback = callback;
  }

  public onStatusChange(callback: OnStatusChangeCallback): void {
    this.onStatusChangeCallback = callback;
  }

  public onError(callback: OnErrorCallback): void {
    this.onErrorCallback = callback;
  }

  private setStatus(status: SpeechRecognitionStatus): void {
    this.status = status;
    this.onStatusChangeCallback?.(status);
  }

  private handleError(error: SpeechRecognitionError): void {
    this.setStatus(SpeechRecognitionStatus.ERROR);
    this.onErrorCallback?.(error);
  }
}
